import { useEffect, useRef, useState } from "react";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from "react-i18next";
import { HeaderProps } from "../../types/dashboard";
import styles from "./style/ProfessionalHeader.module.scss";
import { useSidebarContext } from "../../context/SidebarContext";
import { useNavigate, Link } from "react-router-dom";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useAuthContext } from "../../context/AuthContext";
import { SidebarHeaderIcon } from "../../components/icons";
import classNames from "classnames";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { NotificationDropdown } from "../../components/ui/NotificationDropdown/NotificationDropdown";
import { MessagesDropdown } from "../../components/Landing/Header/MessagesDropdown";
import { useIsMobile } from "../../hooks/use-mobile";
import { localStorageHelper, REDIRECT_AFTER_LOGIN } from "../../utils/localStorageHelper";
import mundoIcon from "../../assets/images/header/mundo.png";
import headerStyles from "../../components/Landing/Header/Header.module.scss";
import "../../assets/styles/official-signed-header.css";

const ProfessionalHeader = ({ title }: HeaderProps) => {
  const { t, i18n } = useTranslation();
  const { toggleSidebar, isSidebarOpen } = useSidebarContext();
  const navigate = useNavigate();
  const { user, logout } = useAuthContext();
  const isMobile = useIsMobile();

  const [imageError, setImageError] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [isCommunityOpen, setIsCommunityOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const languageMenuRef = useRef<HTMLDivElement>(null);
  const communityMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target as Node)) {
        setIsLanguageMenuOpen(false);
      }
      if (communityMenuRef.current && !communityMenuRef.current.contains(event.target as Node)) {
        setIsCommunityOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    setIsMenuOpen(false);
    logout();
    localStorageHelper.removeItem(REDIRECT_AFTER_LOGIN);
    navigate("/");
  };

  const selectLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLanguageMenuOpen(false);
  };

  return (
    <header className={styles.header}>
      <button
        className={styles["header__mobile-menu-button"]}
        onClick={toggleSidebar}
      >
        {isSidebarOpen ? (
          <SidebarHeaderIcon fillColor="#445867" />
        ) : (
          <SidebarHeaderIcon width={25} height={25} fillColor="#445867" />
        )}
      </button>

      {title && (
        <h1 className={styles["header__mobile-title"]}>{title}</h1>
      )}

      <div className={styles["header__content"]}>
        <div className={styles["header__magic-align"]}>
          <div className={headerStyles["header"]} style={{ display: 'contents' }}>
            <div className={headerStyles["header-inner"]} style={{ pointerEvents: 'auto', paddingTop: 0, paddingBottom: 0, height: '100%' }}>
            
            {/* Invisible logo spacer to align correctly with the 3-column grid */}
            <div className={headerStyles["logo"]} style={{ visibility: 'hidden' }}>
              <img src="/logo-placeholder.png" alt="logo" style={{ height: '42px', width: '135px' }} />
            </div>

            {/* Enlaces públicos al lado izquierdo (o centro) */}
            <nav className={headerStyles["nav"]}>
              <div className={headerStyles["nav-links"]}>
                <Link to="/" className={headerStyles["nav-link"]}>{t('landing.header.about', 'Acerca de')}</Link>
                <Link to="/" className={headerStyles["nav-link"]}>{t('landing.header.howItWorks', '¿Cómo funciona?')}</Link>

                <div className={headerStyles['nav-dropdown']} ref={communityMenuRef}>
                  <button
                    type="button"
                    className={classNames(headerStyles['nav-trigger'], {
                      [headerStyles['nav-trigger--active']]: isCommunityOpen
                    })}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsCommunityOpen(prev => !prev);
                    }}
                  >
                    {t('landing.header.community', 'Comunidad')}
                    <ExpandMoreIcon className={headerStyles['arrow-icon']} />
                  </button>

                  <div className={classNames(headerStyles['nav-menu'], { [headerStyles['nav-menu--open']]: isCommunityOpen })} aria-label="Opciones de comunidad">
                    <Link
                      to="/professionals"
                      onClick={() => setIsCommunityOpen(false)}
                      className={headerStyles['nav-link']}
                    >
                      {t('landing.header.seeProfessionals', 'Ver profesionales')}
                    </Link>
                    <Link
                      to="/profesiones-y-practicas"
                      onClick={() => setIsCommunityOpen(false)}
                      className={headerStyles['nav-link']}
                    >
                      {t('landing.header.professionsAndPractices', 'Profesiones y prácticas')}
                    </Link>
                    <Link
                      to="/profesionales-landing"
                      onClick={() => setIsCommunityOpen(false)}
                      className={headerStyles['nav-link']}
                    >
                      {t('landing.header.joinProfessional', 'Unirme como profesional')}
                    </Link>
                  </div>
                </div>

                <a href={process.env.REACT_APP_BLOG_URL} target="_blank" rel="noreferrer" className={headerStyles["nav-link"]}>{t('landing.header.blog', 'Blog')}</a>
                <Link to="/contact" className={headerStyles["nav-link"]}>{t('landing.header.contact', 'Contacto')}</Link>
              </div>
            </nav>

            {/* Acciones a la derecha */}
            <div className={headerStyles["actions"]}>
              {/* Idioma */}
              <div className={headerStyles['action-item']} ref={languageMenuRef}>
                  <button
                      className={classNames(headerStyles['lang-switch'], {
                          [headerStyles['lang-switch--active']]: isLanguageMenuOpen
                      })}
                      type="button"
                      onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                  >
                      <img src={mundoIcon} alt="" aria-hidden="true" width="20" />
                      <span>{i18n.language.toUpperCase()}</span>
                  </button>
                  {isLanguageMenuOpen && (
                      <div className={classNames(headerStyles['dropdown'], headerStyles['dropdown--lang'])}>
                          <button 
                              onClick={() => selectLanguage('es')}
                              className={classNames(headerStyles['dropdown-item'], {
                                  [headerStyles['dropdown-item--active']]: i18n.language === 'es'
                              })}
                          >
                              <div className={headerStyles['dropdown-icon-wrapper']}>
                                  <span className={headerStyles['lang-abbr']}>ES</span>
                              </div>
                              <span>{t('landing.header.spanish', 'Español')}</span>
                          </button>
                          <button 
                              onClick={() => selectLanguage('en')}
                              className={classNames(headerStyles['dropdown-item'], {
                                  [headerStyles['dropdown-item--active']]: i18n.language === 'en'
                              })}
                          >
                              <div className={headerStyles['dropdown-icon-wrapper']}>
                                  <span className={headerStyles['lang-abbr']}>EN</span>
                              </div>
                              <span>{t('landing.header.english', 'Inglés')}</span>
                          </button>
                      </div>
                  )}
              </div>

              <div className="bw-signed-actions">
                <MessagesDropdown />
                <NotificationDropdown />

                <div ref={menuRef} style={{ position: 'relative', display: 'inline-block' }}>
                  <button
                    className="bw-header-user"
                    onClick={() => setIsMenuOpen((prev) => !prev)}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    {user?.details?.image && typeof user.details.image === 'string' && !imageError ? (
                      <img 
                        src={user.details.image.startsWith('http') ? user.details.image : `${process.env.REACT_APP_API_URL}${user.details.image}`} 
                        alt={user.first_name || 'User'} 
                        style={{ width: '30px', height: '30px', borderRadius: '50%', objectFit: 'cover' }}
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <AccountCircleIcon style={{ width: '30px', height: '30px', color: '#65756e' }} />
                    )}
                    <strong>{user?.first_name}</strong>
                    {!isMobile && (
                      <svg viewBox="0 0 16 16" aria-hidden="true" style={{ transform: isMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                        <path d="m4 6 4 4 4-4"></path>
                      </svg>
                    )}
                  </button>

                  {isMenuOpen && (
                    <div className={headerStyles["dropdown"]}>
                      <div className={headerStyles['dropdown-header']}>
                          <p className={headerStyles['dropdown-user-name']}>{user?.first_name} {user?.last_name}</p>
                          <p className={headerStyles['dropdown-user-email']}>{user?.email}</p>
                      </div>
                      <div className={headerStyles['dropdown-divider']} />
                      <Link to="/dashboard" className={headerStyles["dropdown-item"]} onClick={() => setIsMenuOpen(false)}>
                        <div className={headerStyles['dropdown-icon-wrapper']}><HomeOutlinedIcon /></div>
                        <span>Mi panel</span>
                      </Link>
                      <Link to="/profile" className={headerStyles["dropdown-item"]} onClick={() => setIsMenuOpen(false)}>
                        <div className={headerStyles['dropdown-icon-wrapper']}><PersonOutlineIcon /></div>
                        <span>Mi perfil</span>
                      </Link>
                      <div className={headerStyles['dropdown-divider']} />
                      <button type="button" onClick={handleLogout} className={classNames(headerStyles["dropdown-item"], headerStyles["dropdown-item--logout"])}>
                        <span>Cerrar sesión</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default ProfessionalHeader;
